import type { SortEvent } from 'primeng/api';

export type SortCycle = 'asc' | 'desc' | 'none';

export type RemovableSortState<T> = {
  activeField: string | null;
  cycle: SortCycle;
  snapshot: readonly T[];
};

function compareBooleans(left: boolean, right: boolean, order: number): number {
  if (left === right) {
    return 0;
  }
  return order * (left ? -1 : 1);
}

/** Compares two cell values for client-side table sorting. */
export function compareSortValues(
  left: unknown,
  right: unknown,
  order: number
): number {
  if (
    (left === null || left === undefined) &&
    right !== null &&
    right !== undefined
  ) {
    return -order;
  }
  if (
    left !== null &&
    left !== undefined &&
    (right === null || right === undefined)
  ) {
    return order;
  }
  if (
    (left === null || left === undefined) &&
    (right === null || right === undefined)
  ) {
    return 0;
  }
  if (typeof left === 'string' && typeof right === 'string') {
    return order * left.localeCompare(right);
  }
  if (typeof left === 'number' && typeof right === 'number') {
    return order * (left - right);
  }
  if (typeof left === 'boolean' && typeof right === 'boolean') {
    return compareBooleans(left, right, order);
  }
  return order * String(left).localeCompare(String(right));
}

/**
 * Applies removable sort (asc → desc → restore) for PrimeNG `customSort` tables.
 */
export function applyRemovableSort<T>(
  rows: readonly T[],
  event: SortEvent,
  state: RemovableSortState<T>,
  resolveValue: (row: T, field: string) => unknown
): { rows: T[]; state: RemovableSortState<T>; resetTable: boolean } {
  const { field } = event;
  if (typeof field !== 'string') {
    return { rows: [...rows], resetTable: false, state };
  }

  let { activeField, cycle, snapshot } = state;

  if (activeField !== field || cycle === 'none') {
    activeField = field;
    cycle = 'asc';
    snapshot = [...rows];
  } else if (cycle === 'asc') {
    cycle = 'desc';
  } else {
    return {
      rows: [...snapshot],
      resetTable: true,
      state: { activeField: null, cycle: 'none', snapshot: [] },
    };
  }

  const order = cycle === 'asc' ? 1 : -1;
  const sorted = [...rows].sort((rowA, rowB) =>
    compareSortValues(
      resolveValue(rowA, field),
      resolveValue(rowB, field),
      order
    )
  );

  return {
    resetTable: false,
    rows: sorted,
    state: { activeField, cycle, snapshot },
  };
}
